import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  ForwardedRef,
  useImperativeHandle,
  JSX
} from 'react';
import {
  ApiAirportType,
  ApiFlightSearchGeoUnitsRequest,
  ApiFlightSearchGeoUnitsRequestFromJSON,
  ApiGeoUnit,
  ApiItemType,
  ApiLocation,
  ApiSearchGeoUnitsRequest,
  ApiSearchGeoUnitsRequestFromJSON
} from '@ibe/api';
import { GroupBase, OptionsOrGroups, Props as SelectComponentsProps } from 'react-select';
import { DataTestIdProp } from '../../../../Models/DataTestIdProp';
import fallback from '../../../../_Translations/generated/geo-unit-select-async.en.t.json';
import Keys from '../../../../_Translations/generated/geo-unit-select-async.en.t.json.keys';
import useApi from '../../../Hooks/useApi';
import useAppService from '../../../Hooks/useAppService';
import useTranslation from '../../../Hooks/useTranslation';
import SelectAsyncPaginate from '../../Form/SelectAsyncPaginate/SelectAsyncPaginate';
import { debounce } from 'lodash-es';
import { Select } from '../../Form/Select/Select';
import { LoggerFactory } from '@ibe/services';

const logger = LoggerFactory.get('GeoUnitAsyncSelect');

const anywhereGeoUnit: ApiGeoUnit = {
  id: '',
  name: '',
  code: '',
  typeCode: '',
  children: {}
};

export interface GeoUnitSelectAsyncProps extends SelectComponentsProps {
  itemType: ApiItemType;
  airportType?: ApiAirportType;
  onSelect: (geoUnit?: ApiGeoUnit) => void;
  onChange?: (geoUnit?: unknown) => void;
  value?: ApiGeoUnit | undefined | null;
  code?: string;
  displayUnitCode?: boolean;
  placeholder?: string;
  hideAnywhereGeoUnit?: boolean;
  noFilterAnywhereGeoUnit?: boolean;
  menuPortalTarget?: HTMLElement;
  selectLabelSort?: boolean;
  allowedLocations?: ApiLocation[];
  manualLoading?: boolean;
  inputValue?: string;
  id?: string;
}

const GeoUnitSelectAsync = forwardRef<
  {
    resetManualInput: () => void;
    loadOptions?: (() => Promise<ApiGeoUnit[]>) | undefined;
  },
  GeoUnitSelectAsyncProps & DataTestIdProp
>(function GeoUnitSelectAsync(
  props: Omit<GeoUnitSelectAsyncProps & DataTestIdProp, 'ref'>,
  imperativeHandleRef: ForwardedRef<{
    resetManualInput: () => void;
    loadOptions?: () => Promise<ApiGeoUnit[]>;
  }>
): JSX.Element {
  const api = useApi();
  const appService = useAppService();

  const { t } = useTranslation('geo-unit-select-async', fallback, Keys);
  const {
    value,
    code,
    placeholder,
    displayUnitCode,
    onSelect,
    onChange,
    itemType,
    airportType,
    hideAnywhereGeoUnit,
    dataTestId,
    noFilterAnywhereGeoUnit,
    menuPortalTarget,
    selectLabelSort,
    allowedLocations,
    manualLoading,
    inputValue,
    id
  } = props as GeoUnitSelectAsyncProps & DataTestIdProp;

  const [manualOptions, setManualOptions] = useState<ApiGeoUnit[] | undefined>(undefined);
  const [manualInputValue, setManualInputValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useImperativeHandle(imperativeHandleRef, () => ({
    resetManualInput: () => {
      setManualInputValue('');
    },
    loadOptions: async () => {
      let searchOptions: ApiGeoUnit[] = [];
      try {
        setIsLoading(true);
        const result = await loadedOptions('', []);
        searchOptions = result.options;
      } catch (err) {
        logger.error(err);
      } finally {
        setIsLoading(false);
      }
      return searchOptions;
    }
  }));

  const anywhere = useMemo((): ApiGeoUnit => {
    return { ...anywhereGeoUnit, ...{ name: t(Keys.defaultDirection) } };
  }, [t]);

  const [selectedValue, setSelectedValue] = useState(value);

  useEffect((): void => {
    if (!hideAnywhereGeoUnit && !value && !placeholder) {
      setSelectedValue(anywhere);
    } else {
      setSelectedValue(value);
    }
  }, [hideAnywhereGeoUnit, value, placeholder, anywhere]);

  const loadedOptions = useCallback(
    async (
      search: string,
      loadedOption: OptionsOrGroups<ApiGeoUnit, GroupBase<ApiGeoUnit>>
    ): Promise<{ options: ApiGeoUnit[]; hasMore: boolean }> => {
      const request = airportType
        ? ApiFlightSearchGeoUnitsRequestFromJSON({
            type: 'FlightSearchGeoUnitsRequest',
            search,
            offset: loadedOption.length,
            limit: 100,
            itemType,
            airportType,
            languageCode: appService.lang
          } as ApiFlightSearchGeoUnitsRequest)
        : ApiSearchGeoUnitsRequestFromJSON({
            type: 'SearchGeoUnitsRequest',
            search,
            offset: loadedOption.length,
            limit: 100,
            itemType,
            languageCode: appService.lang
          } as ApiSearchGeoUnitsRequest);

      const result =
        appService.config.useIndexedGeoUnits && ApiItemType.TRANSPORT === itemType
          ? await api.searchGeoUnitsElastic(request)
          : await api.searchGeoUnits(request);

      let options: ApiGeoUnit[] = result.geoUnits;
      if (selectLabelSort) {
        options.sort((a, b) => a.name.localeCompare(b.name));
      }

      if (!hideAnywhereGeoUnit && !placeholder) {
        if (noFilterAnywhereGeoUnit && options.length > 0 && loadedOption.length === 0) {
          options.unshift(anywhere);
        } else {
          const regExp = new RegExp(search, 'i');
          if (regExp.test(anywhere.name)) {
            options.unshift(anywhere);
          }
        }
      }

      if (!value && code) {
        setSelectedValue(options.find(geoUnit => geoUnit.code === code));
      }

      if (!!allowedLocations && manualLoading) {
        options = options.filter(
          option => !!allowedLocations.find(allowed => allowed.code === option.code)
        );
        if (!!value && !options.find(option => option.code === value.code)) {
          onSelect(undefined);
          if (onChange) {
            onChange(undefined);
          }
        }
      }

      return {
        options,
        hasMore: result.hasMore
      };
    },
    [
      airportType,
      allowedLocations,
      manualLoading,
      selectLabelSort,
      itemType,
      appService,
      api,
      hideAnywhereGeoUnit,
      noFilterAnywhereGeoUnit,
      placeholder,
      anywhere,
      onSelect,
      onChange
    ]
  );

  const makeRequest = useCallback(
    async (newValue: string): Promise<void> => {
      try {
        setIsLoading(true);
        const result = await loadedOptions(newValue, []);
        setManualOptions(result.options);
      } catch (err) {
        logger.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [loadedOptions]
  );

  const getManualOptions = useCallback(
    debounce(async (): Promise<void> => {
      await makeRequest(manualInputValue);
    }, 300),
    [manualInputValue, loadedOptions]
  );

  useEffect(() => {
    if (manualLoading && !!manualInputValue) {
      getManualOptions();
    }
  }, [manualInputValue]);

  const getLabel = (option: ApiGeoUnit): string => {
    return displayUnitCode ? `${option.name} (${option.code})` : option.name;
  };

  const getValue = (option: ApiGeoUnit): string => {
    return option.id;
  };

  const setInputValue = (newValue: string): void => {
    if (manualLoading) {
      setManualInputValue(newValue);
    }
  };

  const handleMenuOpen = async (): Promise<void> => {
    if (manualLoading) {
      await makeRequest(manualInputValue);
    }
  };

  return manualLoading ? (
    <Select
      className={!!selectedValue && !!selectedValue.id ? 'input--filled' : ''}
      isLoading={isLoading}
      placeholder={placeholder}
      selectedOption={selectedValue}
      onChange={(geoUnit: ApiGeoUnit): void => {
        onSelect(geoUnit);
        if (onChange) {
          onChange(geoUnit);
        }
      }}
      getLabel={(option): string => getLabel(option)}
      getValue={(option): string => getValue(option)}
      options={manualOptions || []}
      onMenuOpen={handleMenuOpen}
      menuPortalTarget={menuPortalTarget}
      setInputValue={setInputValue}
      inputValue={inputValue}
      ignoreFilterOption
      id={id}
    />
  ) : (
    <SelectAsyncPaginate
      className={!!selectedValue && !!selectedValue.id ? 'input--filled' : ''}
      dataTestId={dataTestId}
      selectedOption={selectedValue}
      loadOptions={loadedOptions}
      placeholder={placeholder}
      onChange={(geoUnit: ApiGeoUnit): void => {
        onSelect(geoUnit);
        if (onChange) {
          onChange(geoUnit);
        }
      }}
      getLabel={(option): string => getLabel(option)}
      getValue={(option): string => getValue(option)}
      menuPortalTarget={menuPortalTarget}
      id={id}
    />
  );
});

export default GeoUnitSelectAsync;
